// src/components/users/UserManagement.tsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';

const client = generateClient<Schema>();

interface CognitoUser {
  username: string;
  email?: string;
  emailVerified: boolean;
  enabled: boolean;
  userStatus: string;
  createdDate?: string;
  lastModifiedDate?: string;
}

export default function UserManagement() {
  const queryClient = useQueryClient();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data: response, isLoading, error, refetch } = useQuery({
    queryKey: ['cognitoUsers'],
    queryFn: async () => {
      // @ts-ignore - custom query
      const result = await client.queries.listCognitoUsers({
        limit: 60,
      });
      // Parse the JSON string response from AppSync
      const data = typeof result.data === 'string'
        ? JSON.parse(result.data)
        : result.data;
      return data as { users: CognitoUser[]; nextToken?: string };
    },
  });

  const disableMutation = useMutation({
    mutationFn: async (username: string) => {
      // @ts-ignore - custom mutation
      const result = await client.mutations.disableCognitoUser({
        username,
      });
      // Parse JSON string response
      return typeof result.data === 'string' ? JSON.parse(result.data) : result.data;
    },
    onSuccess: (data) => {
      setSuccessMessage(data.message || 'User disabled successfully');
      queryClient.invalidateQueries({ queryKey: ['cognitoUsers'] });
      setTimeout(() => setSuccessMessage(null), 5000);
    },
  });

  const enableMutation = useMutation({
    mutationFn: async (username: string) => {
      // @ts-ignore - custom mutation
      const result = await client.mutations.enableCognitoUser({
        username,
      });
      // Parse JSON string response
      return typeof result.data === 'string' ? JSON.parse(result.data) : result.data;
    },
    onSuccess: (data) => {
      setSuccessMessage(data.message || 'User enabled successfully');
      queryClient.invalidateQueries({ queryKey: ['cognitoUsers'] });
      setTimeout(() => setSuccessMessage(null), 5000);
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async (username: string) => {
      // @ts-ignore - custom mutation
      const result = await client.mutations.resendPasswordReset({
        username,
      });
      // Parse JSON string response
      return typeof result.data === 'string' ? JSON.parse(result.data) : result.data;
    },
    onSuccess: (data) => {
      setSuccessMessage(data.message || 'Password reset email sent');
      setTimeout(() => setSuccessMessage(null), 5000);
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-red-800">Failed to load users: {(error as Error).message}</p>
        <button
          onClick={() => refetch()}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  const users = response?.users || [];

  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      {successMessage && (
        <div className="p-4 bg-green-50 border-b border-green-200">
          <p className="text-green-800 text-sm">{successMessage}</p>
        </div>
      )}

      {(disableMutation.isError || enableMutation.isError || resetPasswordMutation.isError) && (
        <div className="p-4 bg-red-50 border-b border-red-200">
          <p className="text-red-800 text-sm">
            {(disableMutation.error || enableMutation.error || resetPasswordMutation.error)?.message}
          </p>
        </div>
      )}

      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-800">Cognito Users</h2>
        <button
          onClick={() => refetch()}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          Refresh
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Username
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.username} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {user.username}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-600">
                    {user.email}
                    {user.emailVerified && (
                      <span className="ml-2 text-green-600 text-xs">✓</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge enabled={user.enabled} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-600">{user.userStatus}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-600">
                    {user.createdDate
                      ? new Date(user.createdDate).toLocaleDateString()
                      : '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  {user.enabled ? (
                    <button
                      onClick={() => disableMutation.mutate(user.username)}
                      disabled={disableMutation.isPending}
                      className="text-red-600 hover:text-red-900 disabled:opacity-50"
                    >
                      {disableMutation.isPending ? 'Disabling...' : 'Disable'}
                    </button>
                  ) : (
                    <button
                      onClick={() => enableMutation.mutate(user.username)}
                      disabled={enableMutation.isPending}
                      className="text-green-600 hover:text-green-900 disabled:opacity-50"
                    >
                      {enableMutation.isPending ? 'Enabling...' : 'Enable'}
                    </button>
                  )}
                  <button
                    onClick={() => resetPasswordMutation.mutate(user.username)}
                    disabled={resetPasswordMutation.isPending}
                    className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                  >
                    {resetPasswordMutation.isPending ? 'Sending...' : 'Reset Password'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {users.length === 0 && (
        <div className="p-8 text-center text-gray-500">
          No users found
        </div>
      )}
    </div>
  );
}

function StatusBadge({ enabled }: { enabled: boolean }) {
  return (
    <span
      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
        enabled
          ? 'bg-green-100 text-green-800'
          : 'bg-red-100 text-red-800'
      }`}
    >
      {enabled ? 'Enabled' : 'Disabled'}
    </span>
  );
}
